import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { Application } from 'src/portaljs/constants/application';
import { BreadcrumbItem, ButtonItem, MenuItem, SelectItem } from 'src/portaljs/models/menu-item';
import { Message } from 'src/portaljs/models/message';
import { Notification } from 'src/portaljs/models/notification';
import { ConsoleLogger } from '../shared/console-logger';
import { messageService } from 'src/portaljs/message-service';
import { DestinyViewType, DestinyViewTypeVO } from 'src/portaljs/constants/destiny-view-type';
import { SecondaryToolbar } from 'src/portaljs/models/secondary-toolbar';
import { MessageType } from 'src/portaljs/constants/message-type';

@Injectable({ providedIn: 'root' })
export class ClientStateService {
  public static readonly DESTINY_VIEW_TYPE_STORAGE_KEY = 'dvt';
  // private selectedDestinyViewType: DestinyViewType = DestinyViewType.ALL;

  private clientMenus: Record<Application, { subject: BehaviorSubject<MenuItem[]>, obs$: Observable<MenuItem[]> }> = {} as any;
  private secondaryToolbars: Record<Application, { subject: BehaviorSubject<SecondaryToolbar>, obs$: Observable<SecondaryToolbar> }> = {} as any;
  private pageTitles: Record<Application, string> = {} as any;

  private _notifications: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);
  private notifications$ = this._notifications.asObservable() // order by createdDate desc
                            .pipe( map((nots: Notification[]) => _.orderBy(nots, ['createdDate'], ['desc'])) );

  private _unackMessages: BehaviorSubject<Message[]> = new BehaviorSubject<Message[]>([]);
  private unackMessages$: Observable<Message[]> = this._unackMessages.asObservable();

  private _destinyViewTypesVO: BehaviorSubject<DestinyViewTypeVO> = new BehaviorSubject<DestinyViewTypeVO>(undefined);
  private destinyViewTypesVO$: Observable<DestinyViewTypeVO> = this._destinyViewTypesVO.asObservable();

  constructor() {
    this.initialize();
  }

  public initialize() {
    for (const app of Object.values(Application)) {
      if (app !== Application.PORTAL && app !== Application.DESTINY_EXPRESS && app !== Application.FOLLETT_COMMUNITY && app !== Application.TITLEWAVE) {
        const subject: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>([]);
        this.clientMenus[app] = { subject: subject, obs$: subject.asObservable()};
        const secondarySubject: BehaviorSubject<SecondaryToolbar> = new BehaviorSubject<SecondaryToolbar>({breadcrumbItems: [], buttonItems: [], selectItems: []});
        this.secondaryToolbars[app] = { subject: secondarySubject, obs$: secondarySubject.asObservable()};
      }
    }
  }

  // MessageType.destinyViewType
  public setDestinyViewTypeVO(viewType: DestinyViewTypeVO) {
    this._destinyViewTypesVO.next(viewType);
    this.updateSecondaryToolbarWithDestinyViewType(viewType);
  }
  public getDestinyViewTypeVO$(): Observable<DestinyViewTypeVO> {
    return this.destinyViewTypesVO$;
  }
  public getDestinyViewTypeVO(): DestinyViewTypeVO {
    return this._destinyViewTypesVO.value;
  }
  public getDestinyViewTypes$(): Observable<DestinyViewType[]> {
    return this.destinyViewTypesVO$
    .pipe(
      map((vo: DestinyViewTypeVO) => {
        return vo?.viewTypes || [];
      })
    );
  }
  public getDestinyViewTypes(): DestinyViewType[] {
    return this.getDestinyViewTypeVO()?.viewTypes || [];
  }

  public updateSecondaryToolbarItems(app: Application,
                                     type: MessageType.updateSecondaryToolbarBreadcrumbs | MessageType.updateSecondaryToolbarButtons | MessageType.updateSecondaryToolbarSelects,
                                     items: ButtonItem[] | SelectItem[] | BreadcrumbItem[] = []) {
    const secondaryToolbar: SecondaryToolbar = this.getSecondaryToolbar(app) || {selectItems: [], breadcrumbItems: [], buttonItems: [] };
    if (type === MessageType.updateSecondaryToolbarButtons) {
      secondaryToolbar.buttonItems = items as ButtonItem[];
    } else if (type === MessageType.updateSecondaryToolbarSelects) {
      secondaryToolbar.selectItems = items as SelectItem[];
    } else if (type === MessageType.updateSecondaryToolbarBreadcrumbs) {
      secondaryToolbar.breadcrumbItems = items as BreadcrumbItem[];
    }
    this.setSecondaryToolbar([app], secondaryToolbar);
  }

  private updateSecondaryToolbarWithDestinyViewType(dvtvo: DestinyViewTypeVO) {
    if (!dvtvo) { return; }
    const newDestinyViewTypes = dvtvo.viewTypes || [];
    const newDestinyViewTypeSelectOptions: MenuItem[] = newDestinyViewTypes.map((vt: DestinyViewType) => {
      return {id: vt, name: vt, default: vt === dvtvo.selectedViewType};
    });

    const secondaryToolbar: SecondaryToolbar = this.getSecondaryToolbar(Application.DESTINY) || {selectItems: [], breadcrumbItems: [], buttonItems: [] };
    let existingSecondaryDestinyViewTypeSelect: SelectItem = _.find(secondaryToolbar.selectItems, ['id', 'legacy-secondary-destinyViewType']);
    if (!_.isEqual(existingSecondaryDestinyViewTypeSelect?.subMenuItems, newDestinyViewTypeSelectOptions)) {

      if (!existingSecondaryDestinyViewTypeSelect) {
        existingSecondaryDestinyViewTypeSelect = {id: 'legacy-secondary-destinyViewType', name: 'Destiny View Type', subMenuItems: []};
        secondaryToolbar.selectItems.push(existingSecondaryDestinyViewTypeSelect);
      }
      existingSecondaryDestinyViewTypeSelect.subMenuItems = newDestinyViewTypeSelectOptions;
      this.setSecondaryToolbar([Application.DESTINY], secondaryToolbar);
    }
  }


  // MessageType.registerMenuItems
  public setMenuItems(apps: Application[], menuItems: MenuItem[]) {
    ConsoleLogger.debug('updating menuItems for [' + JSON.stringify(apps) + ']');
    for (const app of apps) {
      if (app === Application.DESTINY) { // LCP-428
        const miIndex = menuItems.findIndex(m => m.id === 'MoreMenu');
        if (miIndex >= 0) {
          const mi = menuItems[miIndex];
          if (mi.subMenuItems?.length === 1 && mi.subMenuItems[0].id === 'HELPMENU') {
            const helpMenu = mi.subMenuItems[0];
            helpMenu.icon = mi.icon;
            menuItems[miIndex] = helpMenu;
          }
        }
      }
      this.clientMenus[app]?.subject?.next(menuItems);
    }
  }
  public getMenuItems$(app: Application): Observable<MenuItem[]> {
    if (app === Application.PORTAL) {
      return of([]);
    }
    return this.clientMenus[app].obs$;
  }
  public getMenuItems(app: Application): MenuItem[] {
    if (!app || app === Application.PORTAL) {
      return [];
    }
    return this.clientMenus[app]?.subject.value;
  }

  // MessageType.registerSecondaryToolbar
  public setSecondaryToolbar(apps: Application[] = [], secondaryToolbar: SecondaryToolbar) {
    ConsoleLogger.debug('updating secondaryToolbar for [' + JSON.stringify(apps) + '] : ' + JSON.stringify(secondaryToolbar));
    for (const app of apps) {
      this.secondaryToolbars[app]?.subject?.next(_.cloneDeep(secondaryToolbar));
    }
  }

  public refreshSecondaryToolbarSelectValues(app: Application, selectItem: SelectItem) {
    const stb: SecondaryToolbar = this.getSecondaryToolbar(app);
    if (!stb) { return; }

    for(let i=0; i < stb.selectItems.length; i++) {
      if (stb.selectItems[i].id === selectItem.id) {
        stb.selectItems[i] = selectItem;
        break;
      }
    }
    this.setSecondaryToolbar([app], stb);
  }
  public getSecondaryToolbar$(app: Application): Observable<SecondaryToolbar> {
    if (app === Application.PORTAL) {
      return of(undefined);
    }
    return this.secondaryToolbars[app].obs$;
  }
  public getSecondaryToolbar(app: Application): SecondaryToolbar {
    if (!app || app === Application.PORTAL) {
      return undefined;
    }
    return this.secondaryToolbars[app]?.subject.value;
  }

  // MessageType.updateNavInfo (pageTitle)
  public setPageTitle(app: Application, pageTitle: string) {
    this.pageTitles[app] = pageTitle;
  }
  public getPageTitle(app: Application): string {
    return this.pageTitles?.[app];
  }


  // MessageType.notification
  public setNotifications(app: Application, appNotifications: Notification[]) {
    for (const notification of appNotifications) {
      notification.source = app;
    }

    // Clear pre-existing notifications for the app
    const otherAppNotifications = _.filter(this.getNotifications(), (n) => n.source !== app);

    // Add our app Notifications to the other app Notifications. Notifications are unique by application/id
    const combinedNotifications = _.uniqWith(otherAppNotifications.concat(appNotifications), (n1, n2) => n1.source === n2.source && n1.id === n2.id);
    for (const n of combinedNotifications) {
      n.new = (n.new === true || (n.new as any) === 'true'); // Destiny is not sending a boolean
    }
    this._notifications.next(combinedNotifications);
  }

  public getNotificationsByApp$(app: Application): Observable<Notification[]> {
    return this.getNotifications$()
      .pipe( map((notifications: Notification[]) => _.filter(notifications, (n) => n.source === app )) );
  }

  public getNotificationsByApp(app: Application): Notification[] {
    return _.orderBy(_.filter(this.getNotifications(), (n) => n.source === app), ['createdDate'], ['desc']);
  }

  public getNotifications$(): Observable<Notification[]> {
    return this.notifications$;
  }
  public getNotifications() {
    return _.orderBy(this._notifications.value, ['createdDate'], ['desc']);
  }
  public clearNotifications() {
    this._notifications.next([]);
  }

  public updateNotification(newNotification: Notification) {
    const srcNotifications: Notification[] = _.filter(this.getNotifications(), ['source', newNotification.source]);
    const existingNotification: Notification = _.find(srcNotifications, (n) => n.id === newNotification.id);
    if (existingNotification) {
      Object.assign(existingNotification, newNotification);
      this.setNotifications(newNotification.source, srcNotifications);
    }
  }

  public getUnackMessages(): Message[] {
    return this._unackMessages.value;

  }

  public getUnackMessages$(): Observable<Message[]> {
    return this.unackMessages$;
  }

  public addUnackMessage(msg: Message) {
    if (messageService.isAcknowledgable(msg.type)) {

      const unackMessages = this.getUnackMessages();
      if (!_.some(unackMessages, (m) => m.id === msg.id && m.destination === msg.destination)) { // don't duplicate message
        unackMessages.push(msg);
        this._unackMessages.next(unackMessages);
      }
    }
  }

  public removeUnackMessage(msg: Message) {
    const ackMessages: Message[] = this.getUnackMessages();
    _.remove(ackMessages, (m: Message) => m.id === msg.message.id && m.destination === msg.message.destination); // original message is returned in the ack message.
    this._unackMessages.next(ackMessages);
  }

  public removeUnackMessages(app: Application) {
    ConsoleLogger.debug('Removing unackdMessages for: ' + JSON.stringify(app));
    const ackMessages: Message[] = this.getUnackMessages();
    _.remove(ackMessages, (m: Message) => m.destination === app); // original message is returned in the ack message.
    this._unackMessages.next(ackMessages);
  }


}
